//#region [IMPORT]

// === THIRD-PARTY ===
import React, { useState, useRef, createContext } from 'react';

// === STYLE / MATERIAL UI ===
import { useTheme } from '@mui/material/styles';

// === COMPONENTS ===
import Styled from 'components/Styled/Styled';
import {getHexAlpha} from 'components/styles.utils';
import {useDrop} from './DropContext';
import {PanelDirection} from 'pages/FileViewer/components/Panel/PanelData';

//#endregion

export const DragContext = createContext({
  data_type: {},
  data: {},
  drag_ref: null,
})

//-----------------------------------------------------------------
export const GroupDrag = React.memo( ( props ) => {
  //-----------------------------------------------------------------

  //#region [VALUES]

  const { data_type='item', data={}, direction, children, styles, actions, moveItem } = props;
  
  const theme = useTheme();
  const { getDrag } = useDrop();

  const ref = useRef();
  
  const [ is_drop        , setIsDrop       ] = useState(false)
  const [ is_drop_before , setIsDropBefore ] = useState(false);

  const is_col = direction === PanelDirection.COL;

  //#endregion

  //#region [STYLES]

  const CONTAINER = {
    onDragLeave: onDragLeave,
    onDragOver : onDragOver ,
    onDragEnd  : onDragEnd  ,
    onDrop     : onDrop     ,
  }

  //#endregion

  //#region [EVENTS]

  //-----------------------------------------------------------------
  function onDragLeave( event ) {
    //-----------------------------------------------------------------
    const { type, value } = getDrag();
    if ( type == null ) return;
    if ( type !== data_type ) return;
    if ( value.id === data.id ) return;
    event.stopPropagation();
    event.preventDefault();
    
    const rect = ref.current.getBoundingClientRect();
    if(event.clientY < rect.top || event.clientY >= rect.bottom || event.clientX < rect.left || event.clientX >= rect.right) {
      setIsDrop( false );
    }
  }

  //-----------------------------------------------------------------
  function onDragOver( event ) {
    //-----------------------------------------------------------------
    const { type, value } = getDrag();
    if ( type == null ) return;
    if ( type !== data_type ) return;
    if ( value.id === data.id ) return;
    event.stopPropagation();
    event.preventDefault();
    const is_directly_before = data.index - value.index ===  1;
    const is_directly_after  = data.index - value.index === -1;
    
    const bounding = ref.current.getBoundingClientRect();
    const offset_y   = bounding.y + (bounding.height/2);
    const offset_x   = bounding.x + (bounding.width /2);
    
    let is_mouse_before = false;
    let is_mouse_after  = false;
    if(  is_col ) is_mouse_before = event.clientX - offset_x <= 0;
    if(  is_col ) is_mouse_after  = event.clientX - offset_x >  0;
    if( !is_col ) is_mouse_before = event.clientY - offset_y <= 0;
    if( !is_col ) is_mouse_after  = event.clientY - offset_y >  0;

    let is_cancelled = false;
    if(  is_col && is_directly_before && is_mouse_before ) is_cancelled = true;
    if(  is_col && is_directly_after  && is_mouse_after  ) is_cancelled = true;
    if( !is_col && is_directly_before && is_mouse_before ) is_cancelled = true;
    if( !is_col && is_directly_after  && is_mouse_after  ) is_cancelled = true;

    if( is_cancelled ) {
      if( is_drop ) {
        setIsDrop( false );
      }
      if( value.is_cancelled == false ) {
        value.is_cancelled = true;
      }
    }
    else {
      if( !is_drop ) {
        setIsDrop( true );
      }
      if( is_drop_before !== is_mouse_before ) {
        setIsDropBefore( is_mouse_before );
      }
    }
  }

  //-----------------------------------------------------------------
  function onDragEnd( event ) {
    //-----------------------------------------------------------------
    event.stopPropagation();
    setIsDrop( false );
  }

  //-----------------------------------------------------------------
  function onDrop( event ) {
    //-----------------------------------------------------------------
    const { type, value } = getDrag();
    if ( type == null ) return;
    if ( type !== data_type ) return;
    if ( value.id === data.id ) return;
    event.stopPropagation();
    event.preventDefault();

    moveItem( value, data, is_drop_before );

    setIsDrop(false);
    setIsDropBefore( false );
  }

  //#endregion

  //#region [RENDER]

  //-----------------------------------------------------------------
  function display() {
    //-----------------------------------------------------------------
    return (
      <DragContext.Provider value={{data_type, drag_data: data, drag_ref: ref }}>
        <Styled ref={ref} styles={[...styles]} {...CONTAINER} >
          {is_drop ? displayPlaceholder() : null }
          {children}
        </Styled>
      </DragContext.Provider>
    )
  }
    
    //-----------------------------------------------------------------
    function displayPlaceholder() {
      //-----------------------------------------------------------------
      const bounding = ref.current.getBoundingClientRect();

      let top  = 0;
      let left = 0;

      let width = 0;
      let height = 0;

      if( is_col ) {
        top  = is_drop_before ? bounding.top  : bounding.top;
        left = is_drop_before ? bounding.left : bounding.left + parseInt( bounding.width  / 2 );
        width  = parseInt( bounding.width / 2 );
        height = bounding.height
      } else {
        top  = is_drop_before ? bounding.top  : bounding.top  + parseInt( bounding.height / 2 );
        left = is_drop_before ? bounding.left : bounding.left;
        width  = bounding.width;
        height = parseInt( bounding.height / 2 )
      }

      return (
        <Styled styles={[
          { position: 'absolute' },
          { zIndex: 100 },
          { top  , left   },
          { width, height },
          { background: theme.palette.primary.main + getHexAlpha(50) }
        ]} />
      )
    }

  //#endregion

  return display();
}
)